import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Headphones } from 'lucide-react';
import { siteConfig } from '../mockData';
import { stripHtmlTags, resolveHeroData, resolveLatestEpisode } from '../utils/heroHelpers';
import { HeroEpisodePlayer } from './HeroEpisodePlayer';
import './Hero.css';

/**
 * CMS-powered Hero component.
 * Accepts CMS data as props but maintains exact HTML structure and styling.
 *
 * Featured Episode Priority:
 *  1. `featuredEpisode` prop
 *  2. cmsEpisodes entry with `isFeaturedInHero === true`
 *  3. Most-recent cmsEpisodes entry
 *
 * Refactored Feb 17, 2026: helpers (`stripHtmlTags`, validation, resolution,
 * player rendering) moved to `/utils/heroHelpers.js` and
 * `HeroEpisodePlayer.jsx` to lower cyclomatic complexity. Behavior unchanged.
 */
export const HeroCMS = ({
  cmsContent = null,
  cmsEpisodes = [],
  featuredEpisode = null,
  siteSettings = null,
  isLoading = false,
}) => {
  // Default hero data (overridden by CMS block content below when present)
  const defaults = {
    videoUrl:
      'https://customer-assets.emergentagent.com/job_tee-time-13/artifacts/n63i5ri4_Gen-3%20Alpha%20Turbo%20718457274%2C%20Create%203%20sequential%20%2C%20202208_Golfers_Journ%2C%20M%205.mp4',
    title: 'The Golf World\'s',
    titleAccent: 'Group Chat',
    subtitle:
      "Just friends, stories, and everything we love about the game.\nIf that's you, you're one of us.",
    showHeroButtons: siteSettings?.showHeroButtons || false,
    buttons: [
      { text: 'Watch Latest Episode', url: '#latest-episode', style: 'primary' },
      { text: 'Listen on Spotify', url: siteConfig.podcast.spotify, style: 'accent' },
    ],
  };

  const heroData = resolveHeroData(defaults, cmsContent);
  const latestEpisodeData = resolveLatestEpisode(featuredEpisode, cmsEpisodes);

  return (
    <section className="hero-section">
      {/* Video Background */}
      <div className="hero-video-container">
        <video autoPlay loop muted playsInline className="hero-video">
          <source src={heroData.videoUrl} type="video/mp4" />
        </video>
        <div className="hero-overlay"></div>
      </div>

      {/* Hero Content */}
      <div className="container hero-content">
        <div className="hero-text">
          <h1 className="hero-title">
            {heroData.title}
            <span className="hero-title-accent"> {heroData.titleAccent}</span>
          </h1>
          <p className="hero-subtitle">
            {heroData.subtitle.split('\n').map((line, i) => (
              <React.Fragment key={`hero-line-${i}-${line.slice(0, 16)}`}>
                {line}
                {i < heroData.subtitle.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
          <div className="hero-cta">
            {heroData.buttons &&
              heroData.showHeroButtons !== false &&
              heroData.buttons.map((button, index) => {
                const isExternal =
                  button.url &&
                  typeof button.url === 'string' &&
                  button.url.startsWith('http');
                return (
                  <a
                    key={`${button.text || 'btn'}-${button.url || ''}-${index}`}
                    href={button.url || '#'}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    className={`btn-${button.style || 'primary'} btn-large`}
                  >
                    {index === 0 ? <Play size={20} /> : <Headphones size={20} />}
                    <span>{button.text || 'Button'}</span>
                  </a>
                );
              })}
          </div>
        </div>

        {/* Latest Episode Card - only render when we have real data to prevent flash */}
        {latestEpisodeData && (
          <div className="hero-episode-card" id="latest-episode">
            <div className="episode-card-header">
              {latestEpisodeData.slug ? (
                <Link
                  to={`/episodes/${latestEpisodeData.slug}`}
                  className="episode-title-link"
                >
                  <h2 className="episode-title">{latestEpisodeData.title}</h2>
                </Link>
              ) : (
                <h2 className="episode-title">{latestEpisodeData.title}</h2>
              )}
              <div className="episode-badge">Latest Episode</div>
            </div>
            <p className="episode-meta">
              {latestEpisodeData.series}
              {latestEpisodeData.duration && ` • ${latestEpisodeData.duration}`}
              {latestEpisodeData.status && ` • ${latestEpisodeData.status}`}
              {latestEpisodeData.publishDate &&
                ` • ${new Date(latestEpisodeData.publishDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}`}
            </p>

            {/* YouTube Player Embed or Episode Thumbnail */}
            <div className="episode-player">
              <HeroEpisodePlayer episode={latestEpisodeData} />
            </div>

            <p className="episode-description">
              {stripHtmlTags(latestEpisodeData.description)}
            </p>

            <div className="episode-actions">
              {latestEpisodeData.slug && (
                <Link
                  to={`/episodes/${latestEpisodeData.slug}`}
                  className="btn-primary"
                >
                  Watch Episode
                </Link>
              )}
              <Link to="/episodes" className="btn-accent">
                View All Episodes
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
